/**
 * Accessibility helpers shared by JS-rendered product cards
 * (collection grid, merch grid, featured collection) and option pickers (PDP, quick-add).
 *
 * - Strikethrough prices get visually hidden "Sale price" / "Regular price" labels
 * - Color swatches are real buttons named by their color, with aria-pressed
 *   marking the one currently shown, so screen readers don't skip them
 * - Sold-out options carry visually hidden "Sold out" text, not only dimmed styling
 */

const strings = () => window.variantStrings || {};

/**
 * Visually hidden label placed before the current price when a strikethrough price is shown
 * @returns {string} HTML string
 */
export function salePriceLabelHTML() {
  return `<span class="sr-only">${strings().salePrice || 'Sale price'}</span>`;
}

/**
 * Strikethrough compare-at price with a visually hidden "Regular price" label
 * @param {string} formattedPrice - Price including currency symbol
 * @param {string} [attrs] - Extra attributes for the <s> element
 * @returns {string} HTML string
 */
export function comparePriceHTML(formattedPrice, attrs = '') {
  return `<span class="sr-only">${
    strings().regularPrice || 'Regular price'
  }</span><s class="line-through text-[#757575]" ${attrs}>${formattedPrice}</s>`;
}

/**
 * Attributes naming a swatch container as the color options group
 * @returns {string} Attribute string
 */
export function swatchGroupAttrs() {
  return `role="group" aria-label="${strings().colorOptions || 'Color options'}"`;
}

/**
 * Interactive swatch button
 * @param {Object} options
 * @param {string|number} options.id - Value for data-swatch
 * @param {boolean} options.active - Whether this swatch is the color shown
 * @param {string} [options.colorClass] - Color class for the swatch fill
 * @param {string} [options.label] - Color name; can be added later via labelSwatch
 * @returns {string} HTML string
 */
export function swatchButtonHTML({ id, active, colorClass = '', label = '' }) {
  return `<button type="button" class="swatch ${active ? 'active' : ''} ${colorClass}" data-swatch="${id}" aria-pressed="${active}"${
    label ? ` aria-label="${label}"` : ''
  }></button>`;
}

/**
 * Non-interactive swatch for products with a single color
 * @param {string} colorClass - Color class for the swatch fill
 * @param {string} label - Color name
 * @returns {string} HTML string
 */
export function staticSwatchHTML(colorClass, label) {
  return `<span class="swatch active ${colorClass}" role="img" aria-label="${label}"></span>`;
}

/**
 * Names a swatch by its color once the color is known
 * @param {HTMLElement} swatch - Swatch element
 * @param {string} color - Color name
 */
export function labelSwatch(swatch, color) {
  if (color && !swatch.hasAttribute('aria-label')) {
    swatch.setAttribute('aria-label', color);
  }
}

/**
 * Toggles the visually hidden "Sold out" text inside an option label so the
 * out-of-stock state is announced, not only shown by dimmed/strikethrough styling
 * @param {HTMLElement} option - The option label
 * @param {boolean} isSoldOut - Whether the option is sold out
 */
export function setSoldOutLabel(option, isSoldOut) {
  let soldOutLabel = option.querySelector('[data-soldout-label]');
  if (!soldOutLabel && isSoldOut) {
    soldOutLabel = document.createElement('span');
    soldOutLabel.className = 'sr-only';
    soldOutLabel.setAttribute('data-soldout-label', '');
    soldOutLabel.textContent = `, ${strings().soldOut || 'Sold out'}`;
    option.appendChild(soldOutLabel);
  }
  soldOutLabel?.classList.toggle('hidden', !isSoldOut);
}

/**
 * Marks one swatch as active (visually and for screen readers) within a card
 * @param {HTMLElement} swatch - Swatch to activate
 * @param {HTMLElement} container - Card containing the swatch set
 */
export function setActiveSwatch(swatch, container) {
  container.querySelectorAll('.swatch').forEach((s) => {
    const isActive = s === swatch;
    s.classList.toggle('active', isActive);
    if (s.tagName === 'BUTTON') {
      s.setAttribute('aria-pressed', String(isActive));
    }
  });
}
